import {
  Box,
  type BoxProps,
  type ConditionalValue,
  Flex,
} from '@chakra-ui/react'
import React, {
  ForwardedRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso'
import { PopoverContent } from '~/components/ui/popover'
import { ZIndex } from '~/types/ThemeTypes'

import { MenuStyleValues, useMenuStyles } from '../componentStyles/Menu.style'
import { MenuItem, MenuListItem } from './MenuItem'

export const END_REACHED_COOLDOWN_MS = 100

export const enum MenuPlacement {
  BottomLeft = 'bottom-left',
  BottomCenter = 'bottom-center',
  BottomRight = 'bottom-right',
  TopLeft = 'top-left',
  TopRight = 'top-right',
  RightTop = 'right-top',
  RightBottom = 'right-bottom',
  LeftTop = 'left-top',
  LeftBottom = 'left-bottom',
}

export type MenuOptions = {
  width?: ConditionalValue<string>
  maxHeight?: ConditionalValue<string>
  placement?: MenuPlacement
}

export type MenuCloseReason = 'escape' | 'tab'

/**
 * Exposes the menu as an ARIA `menu` operable from the keyboard: focus moves
 * to `initialFocusIndex` on open, arrows/Home/End move between items,
 * Enter/Space select, and Escape/Tab call `onClose` so the owner can close
 * the menu and return focus to its trigger.
 */
export type MenuKeyboardOptions = {
  onClose: (reason: MenuCloseReason) => void
  initialFocusIndex?: number
  labelledBy?: string
  /** Lets the trigger reference the menu through `aria-controls`. */
  id?: string
}

/**
 * Renders the menu as the ARIA `listbox` popup of a combobox. Focus stays on
 * the combobox input, which owns the keyboard and points at the highlighted
 * option through `aria-activedescendant`; the menu only reflects
 * `activeIndex` and keeps that option scrolled into view.
 */
export type MenuListboxOptions = {
  id: string
  activeIndex: number
  getOptionId: (index: number) => string
  label?: string
}

type MenuProps<TValue> = {
  items: MenuItem<TValue>[]
  anchorRef?: React.RefObject<HTMLElement>
  onSelect?: (value: TValue, index: number) => string | void
  onScrollEndReached?: () => void
  options?: MenuOptions
  header?: React.ReactNode
  headerWrapperVariant?: 'default' | 'divided'
  keyboard?: MenuKeyboardOptions
  listbox?: MenuListboxOptions
}

const MENU_INDEX_ATTRIBUTE = 'data-menu-index'
const MAX_FOCUS_ATTEMPTS = 10

export const Menu = <TValue,>({
  items,
  anchorRef,
  onSelect,
  onScrollEndReached,
  options,
  header,
  headerWrapperVariant,
  keyboard,
  listbox,
}: MenuProps<TValue>) => {
  const [renderPass, setRenderPass] = useState<number>(0)
  const virtuosoRef = useRef<VirtuosoHandle>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const hasAutoFocused = useRef(false)
  const [endReached, setEndReached] = useState(false)
  const endReachedTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const scrollerRef = React.useRef<HTMLElement | null | Window>(null)
  const boxScrollerParentRef = React.useRef<HTMLDivElement>(null)

  // Store anchor dimensions in state to avoid forced reflow during render
  const [anchorDimensions, setAnchorDimensions] = useState<{
    height: number
    width: number
  } | null>(null)
  const [boxHeight, setBoxHeight] = useState<number | undefined>(undefined)

  // Measure anchor dimensions after render to avoid forced reflow
  useLayoutEffect(() => {
    if (anchorRef?.current) {
      const height = anchorRef.current.clientHeight
      const width = anchorRef.current.clientWidth
      setAnchorDimensions({ height, width })
    }
  }, [anchorRef])

  // Measure list container height after render to avoid forced reflow
  useLayoutEffect(() => {
    const listContainerRef = (scrollerRef?.current as HTMLElement)?.firstChild
      ?.firstChild as HTMLElement | undefined
    if (listContainerRef) {
      setBoxHeight(listContainerRef.clientHeight)
    }
  }, [renderPass])

  const handleItemSelected = (
    e: React.SyntheticEvent,
    value: TValue,
    index: number,
    disabled: boolean,
    itemCallback?: (value: TValue, index: number) => void,
  ) => {
    e.stopPropagation()
    if (disabled) return
    onSelect?.(value, index)
    itemCallback?.(value, index)
  }

  const handleScrollEndReachedInternal = () => {
    if (endReached) {
      return
    }

    //only report scroll end reached if there are items in the menu
    if (!items || items.length <= 0) {
      return
    }

    onScrollEndReached?.()

    // Set cooldown to prevent multiple calls
    setEndReached(true)
    clearTimeout(endReachedTimeoutRef.current)
    endReachedTimeoutRef.current = setTimeout(() => {
      setEndReached(false)
    }, END_REACHED_COOLDOWN_MS)
  }

  useEffect(() => () => clearTimeout(endReachedTimeoutRef.current), [])

  let style: Record<string, any> | undefined

  if (anchorRef?.current && anchorDimensions) {
    const anchorElementHeight = anchorDimensions.height
    const anchorElementWidth = anchorDimensions.width

    const yAdjustment = `calc(${MenuStyleValues.anchoredYGap} + ${anchorElementHeight}px)`
    const xAdjustment = `calc(${MenuStyleValues.anchoredXGap} + ${anchorElementWidth}px)`

    style = {
      position: 'absolute',
      zIndex: ZIndex.popover,
      width: options?.width ?? anchorElementWidth,
    }

    const placement = options?.placement ?? MenuPlacement.BottomLeft
    switch (placement) {
      case MenuPlacement.BottomCenter:
        style.top = yAdjustment
        style.left = '50%'
        style.transform = 'translateX(-50%)'
        break
      case MenuPlacement.BottomLeft:
        style.top = yAdjustment
        style.left = 0
        break
      case MenuPlacement.BottomRight:
        style.top = yAdjustment
        style.right = 0
        break
      case MenuPlacement.TopLeft:
        style.bottom = yAdjustment
        style.left = 0
        break
      case MenuPlacement.TopRight:
        style.bottom = yAdjustment
        style.right = 0
        break
      case MenuPlacement.RightTop:
        style.top = 0
        style.left = xAdjustment
        break
      case MenuPlacement.RightBottom:
        style.bottom = 0
        style.left = xAdjustment
        break
      case MenuPlacement.LeftTop:
        style.top = 0
        style.right = xAdjustment
        break
      case MenuPlacement.LeftBottom:
        style.bottom = 0
        style.right = xAdjustment
        break
    }
  } else {
    style = { width: options?.width ?? MenuStyleValues.defaultWidth }
  }

  const listHeight = useRef(0)
  const computedBoxHeight = listHeight.current
    ? `calc(${listHeight.current}px)`
    : '1px'

  useLayoutEffect(() => {
    const el = boxScrollerParentRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const EDGE_PADDING = 8

    if (rect.left < EDGE_PADDING) {
      el.style.left = '0'
      el.style.right = 'auto'
    } else if (rect.right > viewportWidth - EDGE_PADDING) {
      el.style.right = '0'
      el.style.left = 'auto'
    }
  }, [anchorDimensions, items])

  const shouldBeVisible = useMemo(() => {
    return boxHeight || listHeight.current > 0
  }, [boxHeight, renderPass])

  const styles = useMenuStyles()

  const visibleItems = useMemo(
    () =>
      items.filter((item) => {
        if (typeof item.content === 'string') return true
        return !item.content.props?.hide
      }),
    [items],
  )

  const focusItem = useCallback((index: number) => {
    const findItem = () =>
      listRef.current?.querySelector<HTMLElement>(
        `[${MENU_INDEX_ATTRIBUTE}="${index}"]`,
      )
    const item = findItem()
    if (item) {
      item.focus()
      return
    }

    virtuosoRef.current?.scrollToIndex({ index, align: 'center' })
    let attempts = 0
    const focusWhenRendered = () => {
      const renderedItem = findItem()
      if (renderedItem) {
        renderedItem.focus()
      } else if (attempts++ < MAX_FOCUS_ATTEMPTS) {
        requestAnimationFrame(focusWhenRendered)
      }
    }
    requestAnimationFrame(focusWhenRendered)
  }, [])

  useEffect(() => {
    if (!keyboard || hasAutoFocused.current) return
    if (!shouldBeVisible || visibleItems.length === 0) return

    hasAutoFocused.current = true
    const lastIndex = visibleItems.length - 1
    focusItem(Math.min(Math.max(keyboard.initialFocusIndex ?? 0, 0), lastIndex))
  }, [keyboard, shouldBeVisible, visibleItems.length, focusItem])

  const listboxActiveIndex = listbox?.activeIndex ?? -1
  useEffect(() => {
    if (listboxActiveIndex < 0) return
    virtuosoRef.current?.scrollIntoView({ index: listboxActiveIndex })
  }, [listboxActiveIndex])

  const handleMenuKeyDown = (e: React.KeyboardEvent) => {
    if (!keyboard) return

    const lastIndex = visibleItems.length - 1
    const focusedIndex = Number(
      (document.activeElement as HTMLElement | null)
        ?.closest(`[${MENU_INDEX_ATTRIBUTE}]`)
        ?.getAttribute(MENU_INDEX_ATTRIBUTE) ?? -1,
    )

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        focusItem(focusedIndex >= lastIndex ? 0 : focusedIndex + 1)
        break
      case 'ArrowUp':
        e.preventDefault()
        focusItem(focusedIndex <= 0 ? lastIndex : focusedIndex - 1)
        break
      case 'Home':
        e.preventDefault()
        focusItem(0)
        break
      case 'End':
        e.preventDefault()
        focusItem(lastIndex)
        break
      case 'Escape':
        e.preventDefault()
        e.stopPropagation()
        keyboard.onClose('escape')
        break
      case 'Tab':
        keyboard.onClose('tab')
        break
    }
  }

  return (
    <Box css={{ ...styles.container, ...style }} ref={boxScrollerParentRef}>
      {header && (
        <Flex
          css={
            headerWrapperVariant === 'divided'
              ? styles.dividedHeaderContainer
              : styles.headerContainer
          }
        >
          {header}
        </Flex>
      )}

      <Flex
        ref={listRef}
        visibility={shouldBeVisible ? 'visible' : 'hidden'}
        css={styles.scrollerContainer}
        maxHeight={options?.maxHeight ?? MenuStyleValues.defaultMaxHeight}
        height={computedBoxHeight}
        {...(keyboard && {
          role: 'menu',
          id: keyboard.id,
          'aria-labelledby': keyboard.labelledBy,
          onKeyDown: handleMenuKeyDown,
        })}
        {...(listbox && {
          role: 'listbox',
          id: listbox.id,
          'aria-label': listbox.label,
        })}
      >
        <Virtuoso
          ref={virtuosoRef}
          data={visibleItems}
          scrollerRef={(ref) => (scrollerRef.current = ref)}
          // Key rows by their value, not list position, so a shrinking/growing
          // filtered list repaints instead of reusing a stale window. Object
          // values (e.g. ExtensionInstance, PhoneNumber) all stringify to
          // "[object Object]", so fall back to the index for those. Assumes a
          // list uses one value type throughout — a list mixing primitives and
          // objects could collide a primitive "2" with an object row at index 2
          // (no current dropdown does this).
          computeItemKey={(index, item) =>
            item?.value == null || typeof item.value === 'object'
              ? index
              : String(item.value)
          }
          style={{
            display: 'flex',
            height: '100%',
          }}
          totalListHeightChanged={(height) => {
            listHeight.current = height
            setRenderPass((prev) => prev + 1)
          }}
          itemContent={(index, data: MenuItem<TValue>) => {
            const displayContent =
              typeof data.content === 'string' ? (
                <MenuListItem primaryText={data.content} />
              ) : (
                data.content
              )

            const selectItem = (e: React.SyntheticEvent) =>
              handleItemSelected(
                e,
                data.value,
                index,
                data.clickDisabled ?? false,
                data.onSelect,
              )

            return (
              <Flex
                css={styles.itemContainer}
                pt={MenuStyleValues.menuItemsGap}
                key={index}
                onClick={selectItem}
                {...(keyboard && {
                  role: 'menuitem',
                  tabIndex: -1,
                  [MENU_INDEX_ATTRIBUTE]: index,
                  'aria-disabled': data.clickDisabled || undefined,
                  outline: 'none',
                  _focusVisible: {
                    '& > *': {
                      backgroundColor: 'bg.muted',
                      outline: '2px solid',
                      outlineColor: 'brand.fg',
                      outlineOffset: '-2px',
                    },
                  },
                  onKeyDown: (e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      selectItem(e)
                    }
                  },
                })}
                {...(listbox && {
                  role: 'option',
                  id: listbox.getOptionId(index),
                  'aria-selected': index === listbox.activeIndex,
                  'aria-disabled': data.clickDisabled || undefined,
                  'data-highlighted':
                    index === listbox.activeIndex ? '' : undefined,
                  _highlighted: {
                    '& > *': {
                      backgroundColor: 'bg.muted',
                      outline: '2px solid',
                      outlineColor: 'brand.fg',
                      outlineOffset: '-2px',
                    },
                  },
                })}
              >
                {displayContent}
              </Flex>
            )
          }}
          endReached={handleScrollEndReachedInternal}
          overscan={500}
        />
      </Flex>
    </Box>
  )
}

export const MenuPositioningWrapper = React.forwardRef<
  HTMLDivElement,
  BoxProps
>((props: BoxProps, ref: ForwardedRef<HTMLDivElement>) => {
  return (
    <Box ref={ref} position="relative">
      {props.children}
    </Box>
  )
})

export const MenuPopoverWrapper = React.forwardRef<HTMLDivElement, BoxProps>(
  ({ children, ...rest }: BoxProps, ref: ForwardedRef<HTMLDivElement>) => {
    return (
      <Flex
        ref={ref}
        zIndex={ZIndex.popover}
        position="relative"
        css={{ ...rest }}
      >
        <PopoverContent width="fit-content" bg="transparent">
          {children}
        </PopoverContent>
      </Flex>
    )
  },
)
